import SubscriptionService from "@/features/backoffice/api/subscriptions/subscriptions.api";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  MultipleActiveSubscriptionsParams,
  MultipleActiveSubscriptionsResponse,
  SubscriptionData,
  SubscriptionProvider,
  SynchronizationResult,
} from "./subscription.types";

// Définition de l'état initial
interface SubscriptionState {
  verificationLoading: boolean;
  verificationError: string | null;
  verificationResult: SubscriptionData | null;
  synchronizationLoading: boolean;
  synchronizationError: string | null;
  synchronizationResult: SynchronizationResult | null;
  multipleActiveSubscriptionsLoading: boolean;
  multipleActiveSubscriptionsError: string | null;
  multipleActiveSubscriptionsData: MultipleActiveSubscriptionsResponse | null;
  currentStep: number;
}

const initialState: SubscriptionState = {
  verificationLoading: false,
  verificationError: null,
  verificationResult: null,
  synchronizationLoading: false,
  synchronizationError: null,
  synchronizationResult: null,
  multipleActiveSubscriptionsLoading: false,
  multipleActiveSubscriptionsError: null,
  multipleActiveSubscriptionsData: null,
  currentStep: 0,
};

// Thunks avec createAsyncThunk
export const verifySubscription = createAsyncThunk(
  "subscription/verify",
  async (
    {
      subscriptionId,
      provider,
    }: { subscriptionId: string; provider: SubscriptionProvider },
    { rejectWithValue }
  ) => {
    try {
      const result = await SubscriptionService.verifySubscription(
        subscriptionId,
        provider
      );

      console.log(result);

      // Vérification que les données sont valides
      if (!result || !result.subscription_id) {
        throw new Error("L'abonnement n'a pas pu être trouvé");
      }

      // Log pour débogage
      console.log("Résultat de vérification:", result);

      return result;
    } catch (error: any) {
      const errorMessage =
        error.message || "Une erreur est survenue lors de la vérification";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const synchronizeSubscription = createAsyncThunk(
  "subscription/synchronize",
  async (
    subscriptionData: {
      subscription_id: string;
      type: string;
    },
    { rejectWithValue }
  ) => {
    try {
      // Vérification que les données à synchroniser sont valides
      if (!subscriptionData || !subscriptionData.subscription_id) {
        throw new Error(
          "Données d'abonnement invalides pour la synchronisation"
        );
      }

      console.log(subscriptionData);

      const result = await SubscriptionService.synchronizeSubscription(
        subscriptionData
      );

      if (result.data) {
        toast.success(result.message || "Abonnement synchronisé avec succès");
      }

      return result;
    } catch (error: any) {
      const errorMessage =
        error.message || "Une erreur est survenue lors de la synchronisation";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const getMultipleActiveSubscriptions = createAsyncThunk(
  "subscription/getMultipleActive",
  async (
    params: MultipleActiveSubscriptionsParams = {},
    { rejectWithValue }
  ) => {
    try {
      const result = await SubscriptionService.getMultipleActiveSubscriptions(
        params
      );

      // Log pour débogage
      console.log("Résultat de récupération des comptes multiples:", result);

      return result;
    } catch (error: any) {
      const errorMessage =
        error.message ||
        "Une erreur est survenue lors de la récupération des comptes";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Création du slice
const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    resetSubscriptionState: () => initialState,
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    clearVerificationResult: (state) => {
      state.verificationResult = null;
      state.verificationError = null;
    },
    clearSynchronizationResult: (state) => {
      state.synchronizationResult = null;
      state.synchronizationError = null;
    },
    clearMultipleActiveSubscriptions: (state) => {
      state.multipleActiveSubscriptionsData = null;
      state.multipleActiveSubscriptionsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Gestion de la vérification
      .addCase(verifySubscription.pending, (state) => {
        state.verificationLoading = true;
        state.verificationError = null;
      })
      .addCase(
        verifySubscription.fulfilled,
        (state, action: PayloadAction<SubscriptionData>) => {
          state.verificationLoading = false;
          state.verificationResult = action.payload || null;
          // Passage à l'étape de résultat seulement si les données sont valides
          if (action.payload?.subscription_id) {
            state.currentStep = 1;
          }
        }
      )
      .addCase(verifySubscription.rejected, (state, action) => {
        state.verificationLoading = false;
        state.verificationError =
          (action.payload as string) || "Une erreur est survenue";
      })

      // Gestion de la synchronisation
      .addCase(synchronizeSubscription.pending, (state) => {
        state.synchronizationLoading = true;
        state.synchronizationError = null;
      })
      .addCase(
        synchronizeSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.synchronizationLoading = false;
          state.synchronizationResult = action.payload || null;
        }
      )
      .addCase(synchronizeSubscription.rejected, (state, action) => {
        state.synchronizationLoading = false;
        state.synchronizationError =
          (action.payload as string) || "Une erreur est survenue";
      })

      // Gestion de la récupération des comptes avec plusieurs abonnements
      .addCase(getMultipleActiveSubscriptions.pending, (state) => {
        state.multipleActiveSubscriptionsLoading = true;
        state.multipleActiveSubscriptionsError = null;
      })
      .addCase(
        getMultipleActiveSubscriptions.fulfilled,
        (state, action: PayloadAction<MultipleActiveSubscriptionsResponse>) => {
          state.multipleActiveSubscriptionsLoading = false;
          state.multipleActiveSubscriptionsData = action.payload;
        }
      )
      .addCase(getMultipleActiveSubscriptions.rejected, (state, action) => {
        state.multipleActiveSubscriptionsLoading = false;
        state.multipleActiveSubscriptionsError =
          (action.payload as string) || "Une erreur est survenue";
      });
  },
});

// Export des actions et du reducer
export const {
  resetSubscriptionState,
  setCurrentStep,
  clearVerificationResult,
  clearSynchronizationResult,
  clearMultipleActiveSubscriptions,
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
